import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useHover } from '@/hooks/useHover'
import { theme } from '@/lib/theme'

interface ClockSettings {
  timezone: string
  format24Hour: boolean
}

/**
 * ClockWidget component that displays and allows setting the local time.
 * Features:
 * - Real-time clock display
 * - Toggle between 12-hour and 24-hour format
 * - Timezone selection
 * - Date display
 */
export default function ClockWidget() {
  const { t, i18n } = useTranslation()
  const { colors } = useThemeMode()
  const [currentTime, setCurrentTime] = useState<Date>(new Date())
  const [showSettings, setShowSettings] = useState(false)
  const { isHovered, hoverProps } = useHover()
  const [settings, setSettings] = useState<ClockSettings>({
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    format24Hour: false,
  })

  // Update time every second
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date()
      setCurrentTime(now)
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const formatTime = useCallback(
    (date: Date, format24Hour: boolean, timezone: string, locale: string) => {
      const options: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: !format24Hour,
        timeZone: timezone,
      }
      return date.toLocaleTimeString(locale, options)
    },
    []
  )

  const formatDate = useCallback(
    (date: Date, timezone: string, locale: string) => {
      const options: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: timezone,
      }
      return date.toLocaleDateString(locale, options)
    },
    []
  )

  const toggleFormat = () => {
    setSettings((prev) => ({ ...prev, format24Hour: !prev.format24Hour }))
  }

  const handleTimezoneChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSettings((prev) => ({ ...prev, timezone: event.target.value }))
  }

  // Common timezones for easy selection
  const commonTimezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Dubai',
    'Australia/Sydney',
    'Pacific/Auckland',
  ]

  const displayTime = formatTime(
    currentTime,
    settings.format24Hour,
    settings.timezone,
    i18n.language
  )
  const displayDate = formatDate(currentTime, settings.timezone, i18n.language)
  const timezoneName =
    settings.timezone.split('/').pop()?.replaceAll('_', ' ') ||
    settings.timezone

  if (showSettings) {
    return (
      <div
        style={{
          backgroundColor: colors.primaryBg,
          border: `1px solid ${colors.primaryBorder}`,
          borderRadius: theme.borderRadius.md,
          padding: theme.spacing.md,
          boxShadow: theme.shadows.sm,
          minWidth: '200px',
        }}
      >
        <div
          style={{
            fontSize: theme.fontSize.sm,
            fontWeight: 'bold',
            color: colors.primaryText,
            marginBottom: theme.spacing.sm,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{t('clock.settings')}</span>
          <button
            onClick={() => setShowSettings(false)}
            style={{
              background: 'none',
              border: 'none',
              color: colors.secondaryText,
              cursor: 'pointer',
              fontSize: theme.fontSize.sm,
              padding: '0',
            }}
            title={t('clock.closeSettings')}
          >
            ✕
          </button>
        </div>

        <div style={{ marginBottom: theme.spacing.sm }}>
          <label
            style={{
              fontSize: theme.fontSize.xs,
              color: colors.secondaryText,
              display: 'block',
              marginBottom: '4px',
            }}
          >
            {t('clock.timeFormat')}
          </label>
          <button
            onClick={toggleFormat}
            style={{
              width: '100%',
              fontSize: theme.fontSize.sm,
              backgroundColor: colors.primaryAccent,
              color: colors.primaryText,
              padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
              borderRadius: theme.borderRadius.sm,
              border: 'none',
              cursor: 'pointer',
              transition: theme.transitions.default,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.hoverAccent
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryAccent
            }}
          >
            {settings.format24Hour ? t('clock.24hour') : t('clock.12hour')}
          </button>
        </div>

        <div>
          <label
            style={{
              fontSize: theme.fontSize.xs,
              color: colors.secondaryText,
              display: 'block',
              marginBottom: '4px',
            }}
          >
            {t('clock.timezone')}
          </label>
          <select
            value={settings.timezone}
            onChange={handleTimezoneChange}
            style={{
              width: '100%',
              fontSize: theme.fontSize.sm,
              backgroundColor: colors.primaryBg,
              color: colors.primaryText,
              padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
              borderRadius: theme.borderRadius.sm,
              border: `1px solid ${colors.primaryBorder}`,
              cursor: 'pointer',
            }}
          >
            {commonTimezones.map((tz) => (
              <option key={tz} value={tz}>
                {tz.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </div>
      </div>
    )
  }

  return (
    <div
      {...hoverProps}
      style={{
        backgroundColor: colors.primaryBg,
        border: `1px solid ${colors.primaryBorder}`,
        borderRadius: theme.borderRadius.md,
        padding: theme.spacing.md,
        boxShadow: isHovered ? theme.shadows.md : theme.shadows.sm,
        transition: theme.transitions.boxShadow,
        cursor: 'default',
        width: '220px',
      }}
    >
      {/* Time Display */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: theme.spacing.sm,
        }}
      >
        <button
          onClick={toggleFormat}
          style={{
            fontSize: theme.fontSize.lg,
            fontWeight: 'bold',
            color: colors.primaryText,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            transition: theme.transitions.default,
            padding: '0',
            fontFamily: theme.fontFamily.mono,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = colors.hoverAccent
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = colors.primaryText
          }}
          title={
            settings.format24Hour
              ? t('clock.switchTo12')
              : t('clock.switchTo24')
          }
        >
          {displayTime}
        </button>
        {isHovered && (
          <button
            onClick={() => setShowSettings(true)}
            style={{
              fontSize: theme.fontSize.xs,
              backgroundColor: 'transparent',
              color: colors.secondaryText,
              padding: theme.spacing.xs,
              borderRadius: theme.borderRadius.sm,
              border: `1px solid ${colors.primaryBorder}`,
              cursor: 'pointer',
              transition: theme.transitions.default,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryAccent
              e.currentTarget.style.color = colors.primaryText
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.color = colors.secondaryText
            }}
            title={t('clock.clockSettings')}
          >
            ⚙
          </button>
        )}
      </div>

      {/* Date Display */}
      <div
        style={{
          fontSize: theme.fontSize.sm,
          color: colors.primaryText,
          marginBottom: theme.spacing.xs,
          fontWeight: '500',
        }}
      >
        {displayDate}
      </div>

      {/* Timezone Display */}
      <div
        style={{
          fontSize: theme.fontSize.xs,
          color: colors.secondaryText,
        }}
      >
        {timezoneName}
      </div>
    </div>
  )
}
